import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';
import { formatDateTime, formatCurrency } from '@hellenica/shared';
import type { Booking, BookingEvent } from '@hellenica/shared';

export default function BookingView() {
  const { id } = useParams();
  const queryClient = useQueryClient();

  const { data: booking, isLoading } = useQuery<Booking>({
    queryKey: ['booking', id],
    queryFn: () => api.get(`/v1/admin/bookings/${id}`),
  });

  const { data: events } = useQuery<{ events: BookingEvent[] }>({
    queryKey: ['booking-events', id],
    queryFn: () => api.get(`/v1/admin/bookings/${id}/events`),
    enabled: !!id,
  });

  const paymentLinkMutation = useMutation({
    mutationFn: (data: { type: 'deposit' | 'balance'; amount: number }) =>
      api.post<{ url: string }>(`/v1/admin/bookings/${id}/payment-link`, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['booking', id] });
      navigator.clipboard.writeText(data.url);
      toast.success('Payment link copied to clipboard');
    },
  });

  if (isLoading || !booking) return <div className="animate-pulse">Loading...</div>;

  return (
    <div className="max-w-4xl">
      <h1 className="text-2xl font-semibold text-gray-900 mb-2">{booking.customer_name}</h1>
      <p className="text-gray-500 mb-8">{booking.customer_email}</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium mb-4">Booking Details</h2>
          <dl className="space-y-3 text-sm">
            <div className="flex justify-between">
              <dt className="text-gray-500">Status</dt>
              <dd className="font-medium">{booking.status.replace('_', ' ')}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Appointment</dt>
              <dd>{booking.appointment_datetime ? formatDateTime(booking.appointment_datetime) : '-'}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Party Size</dt>
              <dd>{booking.party_size}</dd>
            </div>
          </dl>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium mb-4">Payments</h2>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium">Deposit</span>
                <span className={`px-2 py-1 text-xs rounded-full ${booking.deposit_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                  {booking.deposit_status}
                </span>
              </div>
              <p className="text-2xl font-semibold">{formatCurrency(booking.deposit_amount || 0)}</p>
              {booking.deposit_status === 'pending' && booking.deposit_amount && (
                <button
                  onClick={() => paymentLinkMutation.mutate({ type: 'deposit', amount: booking.deposit_amount! })}
                  className="mt-2 text-sm text-brand-bronze hover:underline"
                >
                  {booking.stripe_deposit_link ? 'Copy link' : 'Generate payment link'}
                </button>
              )}
            </div>
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium">Balance</span>
                <span className={`px-2 py-1 text-xs rounded-full ${booking.balance_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                  {booking.balance_status}
                </span>
              </div>
              <p className="text-2xl font-semibold">{formatCurrency(booking.balance_amount || 0)}</p>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium mb-4">Activity</h2>
          <div className="space-y-4">
            {events?.events.map((event) => (
              <div key={event.id} className="flex gap-4 text-sm">
                <span className="text-gray-400">{formatDateTime(event.created_at)}</span>
                <span className="text-gray-700">{event.event_type.replace('_', ' ')}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
